import { Person, PersonType, Thing, ThingEventSnapshot, ThingType } from '@eagle/core-data-types';
import { CommonTrackingLocationUpdateData } from '@eagle/data-function-types';
import { Stack } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Maybe, Nullable, Undefinable } from '../../types';
import { ThingPersonDialogOperation } from '../assign-multiple-dialog/add-thing-person-dialog';
import { FetchOne, FetchOneOfAll } from '../fetch';
import { useBoolFlag, useNumberFlag } from '../flags';
import { Icon } from '../icons';
import { EventLocationData } from '../map';
import { TimelineData } from '../timeline';
import { EntityCard } from './entity-card';
import { EntityCardActions } from './entity-card-actions';
import { CardAction, EntityItems, EntityTypeCacheType } from './entity-journey-types';
import { MinimizedJourney } from './minimized-journey';
import { useHistorySearch } from './use-history-search';
import { exportCsv } from './util';
export interface ExportDataProps {
  endTime: Date;
  events?: ThingEventSnapshot[];
  startTime: Date;
  status: 'pending' | 'rejected' | 'resolved';
  timeLineData?: TimelineData[];
}

interface Props {
  'data-testid'?: string;
  entityItem: EntityItems;
  handleDrawerToggle?: (value: boolean) => void;
  hoveredEventId: Maybe<string>;
  isDrawerOpen?: boolean;
  openDialog: (operationType: ThingPersonDialogOperation, id: string, entityType: EntityTypeCacheType) => void;
  selectedEvent: Maybe<EventLocationData>;
  setHoveredEventId: Dispatch<SetStateAction<Maybe<string>>>;
  setSelectedEvent: Dispatch<SetStateAction<Maybe<EventLocationData>>>;
}

export const EntityJourneyItem: FC<Props> = ({
  entityItem,
  handleDrawerToggle,
  hoveredEventId,
  isDrawerOpen,
  openDialog,
  selectedEvent,
  setHoveredEventId,
  setSelectedEvent,
  ...props }) => {
  const {
    entityVisibilities,
    openAddDateTimePicker,
    removeEntity,
    toggleAllVisibility,
  } = useHistorySearch();
  const { t } = useTranslation(['common', 'track']);
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);
  const open = Boolean(anchorEl);
  const [exportData, setExportData] = useState<ExportDataProps[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [entity, setEntity] = useState<Undefinable<Thing | Person>>();
  const { enqueueSnackbar } = useSnackbar();
  const maxNumberOfEvents = useNumberFlag('track-thing-history-export-to-csv-max-number-of-events-threshold-v2');
  const enableExportCsvFlag = useBoolFlag('track-thing-history-enable-export-to-csv-feature');

  const formatTimeLineData = (entityDisplay: string, event: ThingEventSnapshot, timeLineData: TimelineData): (string | number)[] => {
    const when = DateTime.fromJSDate(event.occurred, { zone: timeLineData.timezone?.id }).setLocale(navigator.language);
    const whenLocal = when.toLocal();
    const eventData = event.data as CommonTrackingLocationUpdateData;
    return [
      entityDisplay,
      event.eventTypeId,
      whenLocal.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS),
      eventData.velocity?.speed || 0,
      eventData.velocity?.heading || 0,
      eventData.location?.address?.slice(0, 4).join(', ') || '',
      eventData.location?.latitude || '',
      eventData.location?.longitude || '',
    ];
  };

  useEffect(() => {
    if (
      !entity
      || !isExporting
      || exportData.length !== entityItem.journeyList.length
      || exportData.some((item) => item.status === 'pending')
    ) return;

    if (
      !exportData.length
      || exportData.some((item) => item.status === 'rejected')
      || exportData.every((item) => !item.events?.length)
    ) {
      enqueueSnackbar(t('track:page.history.no-exportable-events.hint.info'), { variant: 'error' });
      setIsExporting(false);
      setExportData([]);
      return;
    }

    const userTimezoneOffset = DateTime.local().toFormat('ZZZ');
    const startTime = DateTime.fromJSDate(exportData[0].startTime).toFormat("yyyyMMdd'T'HHmm");
    const endTime = DateTime.fromJSDate(exportData[exportData.length - 1].endTime).toFormat("yyyyMMdd'T'HHmm");

    const formattedExportData = exportData.reduce<(string | number)[][]>((arr, { timeLineData, events }) => {
      if (!timeLineData || !events || (maxNumberOfEvents && arr.length >= maxNumberOfEvents)) return arr;
      const timeLineDataMap = timeLineData.reduce<Record<string, TimelineData>>((obj, item) => (item.eventId ? { ...obj, [item.eventId]: item } : obj), {});

      return arr.concat(events.map((event) => {
        return formatTimeLineData(entity.display, event, timeLineDataMap[event.eventId]);
      }));
    }, []);

    const headers: (string | number)[] = [
      'Vehicle',
      'Event Type',
      `Occurred (UTC${userTimezoneOffset})`,
      'Speed',
      'Heading',
      'Address',
      'Latitude',
      'Longitude',
    ];

    exportCsv([headers].concat(formattedExportData), `history_export_${entity.display}_${startTime}_${endTime}.csv`);
    setIsExporting(false);
    setExportData([]);
  }, [enqueueSnackbar, entity, entityItem.journeyList.length, exportData, maxNumberOfEvents, isExporting, t]);

  const getActions = (entityTypeDisplay: string): CardAction[] => [
    {
      name: ThingPersonDialogOperation.ADD,
      icon: <Icon icon="Plus" />,
      isConfirm: false,
      label: t('common:component.entity-journey.action.add-date-time'),
      onClick: () => {
        setAnchorEl(null);
        openAddDateTimePicker(entityItem.id);
      },
    },
    {
      name: ThingPersonDialogOperation.CHANGE,
      icon: <Icon icon="Repeat" data-testid="repeat-icon" />,
      isConfirm: false,
      label: t('common:component.entity-journey.action.change-entity', { entity: entityTypeDisplay }),
      onClick: () => {
        setAnchorEl(null);
        openDialog(ThingPersonDialogOperation.CHANGE, entityItem.id, entityItem.entityType);
      },
    },
    {
      name: ThingPersonDialogOperation.DUPLICATE,
      icon: <Icon icon="Copy" data-testid="copy-icon" />,
      isConfirm: false,
      label: t('common:component.entity-journey.action.duplicate-set'),
      onClick: () => {
        setAnchorEl(null);
        openDialog(ThingPersonDialogOperation.DUPLICATE, entityItem.id, entityItem.entityType);
      },
    },
    {
      name: 'delete',
      confirmPrompt: t('common:common.hint.confirm'),
      icon: <Icon icon="Trash" data-testid="trash-icon" />,
      isConfirm: true,
      label: t('common:common.action.remove'),
      onClick: () => {
        setAnchorEl(null);
        removeEntity(entityItem.id);
      },
    },
    ...(enableExportCsvFlag
      ? [
        {
          name: 'export',
          icon: <Icon icon="DownloadIcon" data-testid="download-icon" />,
          isConfirm: false,
          label: t('track:component.entity-journey.action.export'),
          onClick: () => {
            if (isExporting) return;
            setAnchorEl(null);
            setIsExporting(true);
          },
        },
      ]
      : []),
  ];

  const handleMoreButtonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleVisibilityButtonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    toggleAllVisibility(entityItem.id);
  };

  return (
    <Stack data-testid={props['data-testid']} flexDirection={isDrawerOpen ? 'column-reverse' : 'column'}>
      <Stack display={isDrawerOpen ? 'none' : 'block'}>
        {entityItem.journeyList.map((journey) => (
          <MinimizedJourney
            key={journey.id}
            dataType={entityItem.entity}
            handleDrawerToggle={handleDrawerToggle}
            journey={journey}
          />
        ))}
      </Stack>
      <FetchOne
        id={entityItem.id}
        dataType={entityItem.entity}
        renderFactory={(data: Thing | Person) => {
          setEntity(data);
          return (
            <FetchOneOfAll
              id={data[entityItem.entityTypeKey as keyof (Thing | Person)]}
              dataType={entityItem.entityType}
              renderFactory={(dataType: ThingType | PersonType) =>
                <Box sx={{ display: isDrawerOpen ? 'block' : 'none' }}>
                  <EntityCard
                    data-testid="entity-card"
                    entityDataType={dataType}
                    entityDisplay={data.display}
                    entityItem={entityItem}
                    handleMoreButtonClick={handleMoreButtonClick}
                    handleVisibilityButtonClick={handleVisibilityButtonClick}
                    hoveredEventId={hoveredEventId}
                    isEntityVisible={!!entityVisibilities.find((entity) => entity.entityId === entityItem.id)?.isVisible}
                    isExporting={isExporting}
                    selectedEvent={selectedEvent}
                    setExportData={setExportData}
                    setHoveredEventId={setHoveredEventId}
                    setSelectedEvent={setSelectedEvent}
                  />
                  <EntityCardActions data-testid="entity-actions" actions={getActions(dataType.display)} anchorEl={anchorEl} handleClose={handleClose} open={open} />
                </Box>
              }
            />
          );
        }}
      />
    </Stack>
  );
};
